import { AppWindow, Bot, Database, Plug, SlidersHorizontal, Users, Workflow, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { ApiError } from "@/api";
import type { ListNavGroup } from "@/components/ListNav";
import { ListNavLayout } from "@/components/ListNavLayout";
import { SectionError, SectionLoading } from "@/components/section";
import { NotFoundPage } from "@/features/errors/NotFoundPage";
import { useSpaceHub } from "../detail/useSpaceHub";
import type { SpaceSettingsContext } from "./useSpaceSettings";

/**
 * `/spaces/:uuid/settings/*`: the same list → section frame as Admin and Settings.
 * Editors and admins only; viewers (and non-members) get the normal not-found page.
 */
export function SpaceSettingsLayout() {
  const { t } = useTranslation("spaces", { keyPrefix: "settings" });
  const { spaceUuid = "" } = useParams();
  const hub = useSpaceHub(spaceUuid);
  const base = `/spaces/${spaceUuid}/settings`;

  if (hub.error instanceof ApiError && (hub.error.status === 404 || hub.error.status === 403)) {
    return <NotFoundPage />;
  }
  if (hub.isPending) return <SectionLoading rows={6} />;
  if (!hub.data) return <SectionError onRetry={() => void hub.refetch()} error={hub.error} />;
  if (!hub.data.canEdit) return <NotFoundPage />;

  const groups: ListNavGroup[] = [
    {
      id: "space",
      label: t("group"),
      items: [
        { to: `${base}/general`, label: t("nav.general"), icon: <SlidersHorizontal /> },
        { to: `${base}/members`, label: t("nav.members"), icon: <Users />, hidden: Boolean(hub.data.isPersonal) },
        { to: `${base}/agents`, label: t("nav.agents"), icon: <Bot /> },
        { to: `${base}/knowledge`, label: t("nav.knowledge"), icon: <Database /> },
        { to: `${base}/connectors`, label: t("nav.connectors"), icon: <Plug /> },
        { to: `${base}/apps`, label: t("nav.apps"), icon: <AppWindow /> },
        { to: `${base}/workflows`, label: t("nav.workflows"), icon: <Workflow /> },
        { to: `${base}/quick-actions`, label: t("nav.quickActions"), icon: <Zap /> },
      ],
    },
  ];
  return (
    <ListNavLayout
      basePath={base}
      title={t("crumb")}
      subtitle={hub.data.name}
      trail={[{ label: hub.data.name, to: `/spaces/${spaceUuid}` }]}
      groups={groups}
      defaultTo={`${base}/general`}
      aria-label={t("navLabel")}
      outletContext={{ space: hub.data } satisfies SpaceSettingsContext}
    />
  );
}
